import React, { useState } from 'react';
import { FrequencyType } from './AddTransactionFlow';
import { Icon } from '../Icon';
import './DateSelector.css';

interface DateSelectorProps {
  frequency: FrequencyType;
  value?: Date;
  dayOfWeek?: number;
  dayOfMonth?: number;
  dayAndMonth?: { day: number; month: number };
  onChange: (data: any) => void;
  onBack: () => void;
}

const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const monthDays = Array.from({ length: 31 }, (_, i) => i + 1);

export const DateSelector: React.FC<DateSelectorProps> = ({
  frequency,
  value,
  dayOfWeek,
  dayOfMonth,
  dayAndMonth,
  onChange,
  onBack
}) => {
  // Используем текущее время пользователя или переданную дату
  const [selectedDate, setSelectedDate] = useState<Date>(value || new Date());
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<number>(dayOfWeek ?? 0);
  const [selectedDayOfMonth, setSelectedDayOfMonth] = useState<number>(dayOfMonth ?? 1);
  const [selectedDayAndMonth, setSelectedDayAndMonth] = useState<{ day: number; month: number }>(
    dayAndMonth || { day: 1, month: 1 }
  );

  const renderOneTimeSelector = () => {
    // Для datetime-local нужен формат YYYY-MM-DDTHH:mm
    const formatDateTimeLocal = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return (
      <div className="date-selector__onetime">
        <p className="date-selector__description">Выберите дату и время транзакции</p>
        <input
          type="datetime-local"
          className="date-selector__date-input neo-inset"
          value={formatDateTimeLocal(selectedDate)}
          onChange={(e) => {
            // Парсим локальное datetime
            const newDate = new Date(e.target.value);
            setSelectedDate(newDate);
          }}
        />
        <button
          className="date-selector__submit neo-raised"
          onClick={() => onChange({ date: selectedDate })}
        >
          Далее
        </button>
      </div>
    );
  };

  const renderWeeklySelector = () => {
    // Формат для input type="date" (YYYY-MM-DD)
    const formatDateLocal = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Получаем минимальную дату (сегодня)
    const today = new Date();
    const minDate = formatDateLocal(today);

    // Вычисляем день недели из выбранной даты (JS: 0=Вс, 6=Сб -> конвертируем в 0=Пн, 6=Вс)
    const calculatedDayOfWeek = (selectedDate.getDay() + 6) % 7;

    // Проверка: выбранная дата не в прошлом
    const isPastDate = selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return (
      <div className="date-selector__weekly">
        <p className="date-selector__description">Дата первого пополнения</p>
        
        {/* Календарь для выбора даты */}
        <input
          type="date"
          className="date-selector__date-input neo-inset"
          value={formatDateLocal(selectedDate)}
          min={minDate}
          onChange={(e) => {
            const newDate = new Date(e.target.value + 'T00:00:00');
            setSelectedDate(newDate);
          }}
          style={{ marginBottom: '16px' }}
        />

        {/* Предупреждение о прошлой дате */}
        {isPastDate && (
          <div style={{
            padding: '12px',
            background: 'transparent',
            border: '2px solid #FFC107',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#FFC107'
          }}>
            Нельзя настроить автопополнение на прошлую дату
          </div>
        )}

        {/* Информация */}
        <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '12px', textAlign: 'center' }}>
          Пополнение будет происходить каждую неделю в этот день:
        </p>

        {/* Дни недели (только для визуализации) */}
        <div className="weekday-grid">
          {weekDays.map((day, index) => (
            <div
              key={index}
              className={`weekday-btn neo-raised ${calculatedDayOfWeek === index ? 'weekday-btn--active weekday-btn--readonly' : 'weekday-btn--readonly'}`}
              style={{
                cursor: 'default',
                opacity: calculatedDayOfWeek === index ? 1 : 0.4,
                pointerEvents: 'none',
                border: 'none'
              }}
            >
              {day}
            </div>
          ))}
        </div>

        <button
          className="date-selector__submit neo-raised"
          onClick={() => {
            if (!isPastDate) {
              onChange({ date: selectedDate, dayOfWeek: calculatedDayOfWeek });
            }
          }}
          disabled={isPastDate}
          style={{
            opacity: isPastDate ? 0.5 : 1,
            cursor: isPastDate ? 'not-allowed' : 'pointer'
          }}
        >
          Далее
        </button>
      </div>
    );
  };

  const renderMonthlySelector = () => {
    // Формат для input type="date" (YYYY-MM-DD)
    const formatDateLocal = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Получаем минимальную дату (сегодня)
    const today = new Date();
    const minDate = formatDateLocal(today);

    // Извлекаем день месяца из выбранной даты
    const calculatedDayOfMonth = selectedDate.getDate();

    // Проверка: выбранная дата не в прошлом
    const isPastDate = selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return (
      <div className="date-selector__monthly">
        <p className="date-selector__description">Дата первого пополнения</p>
        
        {/* Календарь для выбора даты */}
        <input
          type="date"
          className="date-selector__date-input neo-inset"
          value={formatDateLocal(selectedDate)}
          min={minDate}
          onChange={(e) => {
            const newDate = new Date(e.target.value + 'T00:00:00');
            setSelectedDate(newDate);
          }}
          style={{ marginBottom: '16px' }}
        />

        {/* Предупреждение о прошлой дате */}
        {isPastDate && (
          <div style={{
            padding: '12px',
            background: 'transparent',
            border: '2px solid #FFC107',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#FFC107'
          }}>
            Нельзя настроить автопополнение на прошлую дату
          </div>
        )}

        {/* Информация о выбранном числе */}
        <div style={{
          padding: '16px',
          background: 'transparent',
          border: '2px solid #667eea',
          borderRadius: '12px',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
            Ежемесячно будет пополнение в это число:
          </p>
          <p style={{ margin: 0, fontSize: '32px', fontWeight: '700', color: '#667eea' }}>
            {calculatedDayOfMonth}
          </p>
        </div>

        <button
          className="date-selector__submit neo-raised"
          onClick={() => {
            if (!isPastDate) {
              onChange({ date: selectedDate, dayOfMonth: calculatedDayOfMonth });
            }
          }}
          disabled={isPastDate}
          style={{
            opacity: isPastDate ? 0.5 : 1,
            cursor: isPastDate ? 'not-allowed' : 'pointer'
          }}
        >
          Далее
        </button>
      </div>
    );
  };

  const renderYearlySelector = () => {
    // Проверяем, является ли выбранная дата в прошлом
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // Создаем дату из выбранных день/месяц в текущем году
    const selectedYearlyDate = new Date(currentYear, selectedDayAndMonth.month - 1, selectedDayAndMonth.day);
    
    // Если дата уже прошла в этом году, берем следующий год
    const isPastInCurrentYear = selectedYearlyDate < today;
    const targetYear = isPastInCurrentYear ? currentYear + 1 : currentYear;
    const targetDate = new Date(targetYear, selectedDayAndMonth.month - 1, selectedDayAndMonth.day);

    // Форматируем дату для отображения
    const formattedDate = targetDate.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });

    return (
      <div className="date-selector__yearly">
        <p className="date-selector__description">Дата первого пополнения</p>
        
        <div className="yearly-inputs">
          <div className="yearly-input-group">
            <label>День</label>
            <select
              className="yearly-select"
              value={selectedDayAndMonth.day}
              onChange={(e) => setSelectedDayAndMonth({ ...selectedDayAndMonth, day: parseInt(e.target.value) })}
            >
              {monthDays.map((day) => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
          <div className="yearly-input-group">
            <label>Месяц</label>
            <select
              className="yearly-select"
              value={selectedDayAndMonth.month}
              onChange={(e) => setSelectedDayAndMonth({ ...selectedDayAndMonth, month: parseInt(e.target.value) })}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                <option key={month} value={month}>
                  {new Date(2024, month - 1).toLocaleString('ru', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Информация о первом пополнении */}
        <div style={{
          padding: '16px',
          background: 'transparent',
          border: '2px solid #667eea',
          borderRadius: '12px',
          marginTop: '16px',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
            {isPastInCurrentYear 
              ? 'Первое пополнение будет в следующем году:' 
              : 'Первое пополнение будет:'}
          </p>
          <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#667eea' }}>
            {formattedDate}
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
            Затем ежегодно в это же число
          </p>
        </div>

        <button
          className="date-selector__submit neo-raised"
          onClick={() => onChange({ 
            dayAndMonth: selectedDayAndMonth,
            date: targetDate
          })}
        >
          Далее
        </button>
      </div>
    );
  };

  const renderDailySelector = () => {
    // Формат для input type="date" (YYYY-MM-DD)
    const formatDateLocal = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Получаем минимальную дату (сегодня)
    const today = new Date();
    const minDate = formatDateLocal(today);

    // Проверка: выбранная дата не в прошлом
    const isPastDate = selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return (
      <div className="date-selector__daily">
        <p className="date-selector__description">Дата начала</p>
        
        {/* Календарь для выбора даты */}
        <input
          type="date"
          className="date-selector__date-input neo-inset"
          value={formatDateLocal(selectedDate)}
          min={minDate}
          onChange={(e) => {
            const newDate = new Date(e.target.value + 'T00:00:00');
            setSelectedDate(newDate);
          }}
          style={{ marginBottom: '16px' }}
        />

        {/* Предупреждение о прошлой дате */}
        {isPastDate && (
          <div style={{
            padding: '12px',
            background: 'transparent',
            border: '2px solid #FFC107',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#FFC107'
          }}>
            Нельзя настроить автопополнение на прошлую дату
          </div>
        )}

        {/* Информация */}
        <div style={{
          padding: '16px',
          background: 'transparent',
          border: '2px solid #667eea',
          borderRadius: '12px',
          marginBottom: '24px'
        }}>
          <div style={{ fontSize: '14px', color: 'var(--color-text-primary)', marginBottom: '8px' }}>
            Автопополнение будет происходить <strong>ежедневно</strong>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
            Начиная с <strong>{selectedDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
          </div>
        </div>

        <button
          className="date-selector__submit neo-raised"
          onClick={() => onChange({ date: selectedDate })}
          disabled={isPastDate}
          style={{
            opacity: isPastDate ? 0.5 : 1,
            cursor: isPastDate ? 'not-allowed' : 'pointer'
          }}
        >
          Далее
        </button>
      </div>
    );
  };

  const renderContent = () => {
    switch (frequency) {
      case 'onetime':
        return renderOneTimeSelector();
      case 'daily':
        return renderDailySelector();
      case 'weekly':
        return renderWeeklySelector();
      case 'monthly':
        return renderMonthlySelector();
      case 'yearly':
        return renderYearlySelector();
      default:
        return null;
    }
  };

  return (
    <div className="date-selector step-content">
      <div className="date-selector__header">
        <button className="date-selector__back neo-raised" onClick={onBack}>
          <Icon name="chevron_left" size={20} />
        </button>
        <div className="date-selector__header-content">
          <h2 className="date-selector__title">Дата</h2>
          <p className="date-selector__subtitle">
            {frequency === 'onetime' && 'Когда была транзакция?'}
            {frequency === 'daily' && 'Выберите дату начала'}
            {frequency === 'weekly' && 'Выберите дату первого пополнения'}
            {frequency === 'monthly' && 'Выберите дату первого пополнения'}
            {frequency === 'yearly' && 'Выберите день и месяц первого пополнения'}
          </p>
        </div>
      </div>

      {renderContent()}
    </div>
  );
};

